import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { API_BASE } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Save, ArrowLeft, Sparkles, ChevronUp, ChevronDown, Eye, RotateCcw, History, Download, Plus, Trash2, Upload, ImageIcon, Loader2, Wand2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';

const SECTION_LABELS = {
  hero: 'Hero Section', pain_points: 'Pain Points', bullets: 'Key Outcomes',
  what_youll_learn: "What You'll Learn", whats_included: "What's Included",
  audio_teaser: 'Audio Teaser', testimonials: 'Testimonials', pricing: 'Pricing Block',
  faq: 'FAQ', guarantee: 'Guarantee', comparison: 'Comparison Table', custom_rich_text: 'Custom Content',
  external_link: 'Companion Tool', how_it_works: 'How It Works', about_wayfinder: 'About Wayfinder',
  product_spotlight: 'Product Spotlight', category_featured: 'Category Featured Products',
  problem_agitation: 'Problem Agitation', solution_mechanism: 'Solution/Mechanism',
  social_proof: 'Social Proof', offer_stack: 'Offer Stack', final_cta: 'Final CTA',
  pdf_content: 'PDF Content Details', about_author: 'About the Author'
};

const COUNTABLE_SECTIONS = {
  faq: { label: 'FAQs', default: 5, min: 1, max: 15 },
  pain_points: { label: 'Pain Points', default: 3, min: 1, max: 10 },
  bullets: { label: 'Key Outcomes', default: 4, min: 1, max: 10 },
  testimonials: { label: 'Testimonials', default: 2, min: 1, max: 8 },
  what_youll_learn: { label: 'Items', default: 3, min: 1, max: 10 },
  problem_agitation: { label: 'Points', default: 3, min: 1, max: 8 },
  social_proof: { label: 'Items', default: 3, min: 1, max: 8 },
};

function ImageUploader({ value, onChange, label }) {
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [prompt, setPrompt] = useState('');
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/upload/image', formData);
      onChange(data.url);
      toast.success('Image uploaded');
    } catch { toast.error('Upload failed'); }
    setUploading(false);
    e.target.value = '';
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) { toast.error('Enter a prompt'); return; }
    setGenerating(true);
    try {
      const { data } = await api.post('/ai/generate-image', { prompt: prompt.trim() });
      onChange(data.url);
      toast.success('Image generated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Generation failed'); }
    setGenerating(false);
  };

  return (
    <div className="space-y-2">
      <Label className="text-xs">{label || 'Image'}</Label>
      {value && (
        <div className="relative">
          <img src={value.startsWith('/api') ? `${API_BASE.replace('/api','')}${value}` : value} alt="" className="w-full h-32 object-cover rounded-lg border" />
          <Button variant="ghost" size="icon" className="absolute top-1 right-1 h-6 w-6 bg-black/50 hover:bg-black/70 text-white" onClick={() => onChange('')}><Trash2 className="w-3 h-3" /></Button>
        </div>
      )}
      <div className="flex gap-1">
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
        <Button variant="outline" size="sm" className="text-xs flex-1" onClick={() => fileRef.current?.click()} disabled={uploading}>
          {uploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
          Upload
        </Button>
        <Input value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="AI prompt..." className="h-8 text-xs flex-[2]" onKeyDown={e => e.key === 'Enter' && handleGenerate()} />
        <Button variant="outline" size="sm" className="text-xs" onClick={handleGenerate} disabled={generating || !prompt.trim()}>
          {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
        </Button>
      </div>
    </div>
  );
}

function RichTextEditor({ value, onChange }) {
  const ref = React.useRef(null);

  const wrap = (before, after) => {
    const ta = ref.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = value.substring(start, end);
    const newVal = value.substring(0, start) + before + selected + after + value.substring(end);
    onChange(newVal);
    setTimeout(() => { ta.focus(); ta.selectionStart = start + before.length; ta.selectionEnd = end + before.length; }, 0);
  };

  const insert = (text) => {
    const ta = ref.current;
    if (!ta) return;
    const pos = ta.selectionStart;
    const newVal = value.substring(0, pos) + text + value.substring(pos);
    onChange(newVal);
    setTimeout(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = pos + text.length; }, 0);
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1 p-1 rounded-t-md border border-b-0 bg-muted/50">
        <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs font-bold" onClick={() => wrap('<strong>', '</strong>')} title="Bold">B</Button>
        <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs italic" onClick={() => wrap('<em>', '</em>')} title="Italic">I</Button>
        <div className="w-px h-4 bg-border mx-0.5" />
        <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => insert('<br/>\n')} title="Line break">BR</Button>
        <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => wrap('<p>', '</p>\n')} title="Paragraph">P</Button>
        <div className="w-px h-4 bg-border mx-0.5" />
        <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => insert('\n<ul>\n  <li></li>\n</ul>\n')} title="Bullet list">List</Button>
        <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => insert('  <li></li>\n')} title="List item">+ Li</Button>
        <div className="w-px h-4 bg-border mx-0.5" />
        <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => wrap('<h3>', '</h3>\n')} title="Heading">H3</Button>
        <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => wrap('<a href="">', '</a>')} title="Link">Link</Button>
      </div>
      <Textarea ref={ref} value={value} onChange={e => onChange(e.target.value)} rows={8} className="text-sm font-mono rounded-t-none border-t-0" placeholder="<p>Your content here...</p>" />
      {value && (
        <div className="p-3 rounded-lg border bg-white dark:bg-card text-sm mt-1">
          <p className="text-[10px] text-muted-foreground mb-1 font-medium">Preview</p>
          <div className="prose prose-sm dark:prose-invert max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5" dangerouslySetInnerHTML={{ __html: value }} />
        </div>
      )}
    </div>
  );
}

function SectionEditor({ section, onChange, onRegenerate, allProducts, allCategories }) {
  const [regenerating, setRegenerating] = useState(false);
  const [genCount, setGenCount] = useState(COUNTABLE_SECTIONS[section.type]?.default || 0);
  const fields = section.fields || {};

  const updateField = (key, value) => onChange({ ...section, fields: { ...fields, [key]: value } });

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const countConfig = COUNTABLE_SECTIONS[section.type];
      const instructions = countConfig ? `Generate exactly ${genCount} ${countConfig.label.toLowerCase()}.` : '';
      await onRegenerate(section.id, instructions);
    } finally { setRegenerating(false); }
  };

  const countConfig = COUNTABLE_SECTIONS[section.type];

  // Hero section has special editor
  if (section.type === 'hero') {
    const headlines = fields.headlines || [fields.headline || ''];
    const subheadlines = fields.subheadlines || [fields.subheadline || ''];
    return (
      <div className="space-y-3 p-4 rounded-lg border bg-card">
        <div className="flex items-center justify-between gap-2">
          <h4 className="font-medium text-sm font-['Outfit']">Hero Section</h4>
          <Button variant="ghost" size="sm" onClick={handleRegenerate} disabled={regenerating} className="text-[#1A9DA5] hover:text-[#158D92]">
            <Sparkles className="w-3 h-3 mr-1" />{regenerating ? 'Generating...' : 'Generate'}
          </Button>
        </div>

        {/* Multiple headlines */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Headlines (random pick on page load)</Label>
            <Button variant="ghost" size="sm" className="text-xs h-6" onClick={() => updateField('headlines', [...headlines, ''])}><Plus className="w-3 h-3 mr-1" />Add</Button>
          </div>
          {headlines.map((h, i) => (
            <div key={i} className="flex gap-1">
              <Input value={h} onChange={e => { const arr = [...headlines]; arr[i] = e.target.value; updateField('headlines', arr); }} className="h-8 text-sm" placeholder={`Headline ${i + 1}`} />
              {headlines.length > 1 && <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => updateField('headlines', headlines.filter((_, j) => j !== i))}><Trash2 className="w-3 h-3 text-destructive" /></Button>}
            </div>
          ))}
        </div>

        {/* Multiple subheadlines */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Subheadlines (random pick on page load)</Label>
            <Button variant="ghost" size="sm" className="text-xs h-6" onClick={() => updateField('subheadlines', [...subheadlines, ''])}><Plus className="w-3 h-3 mr-1" />Add</Button>
          </div>
          {subheadlines.map((s, i) => (
            <div key={i} className="flex gap-1">
              <Input value={s} onChange={e => { const arr = [...subheadlines]; arr[i] = e.target.value; updateField('subheadlines', arr); }} className="h-8 text-sm" placeholder={`Subheadline ${i + 1}`} />
              {subheadlines.length > 1 && <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => updateField('subheadlines', subheadlines.filter((_, j) => j !== i))}><Trash2 className="w-3 h-3 text-destructive" /></Button>}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs">CTA Text</Label><Input value={fields.cta_text || ''} onChange={e => updateField('cta_text', e.target.value)} className="h-8 text-sm" /></div>
          <div className="space-y-1"><Label className="text-xs">CTA URL</Label><Input value={fields.cta_url || ''} onChange={e => updateField('cta_url', e.target.value)} className="h-8 text-sm" /></div>
        </div>

        {/* Checkboxes */}
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={!!fields.include_payment_box} onCheckedChange={v => updateField('include_payment_box', v)} />
            <span className="text-xs font-medium">Include payment box</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={!!fields.include_back_to_store} onCheckedChange={v => updateField('include_back_to_store', v)} />
            <span className="text-xs font-medium">Include back to store button</span>
          </label>
        </div>

        {/* Background image */}
        <ImageUploader value={fields.bg_image || fields.section_image || ''} onChange={url => { updateField('bg_image', url); updateField('section_image', url); }} label="Background Image" />
      </div>
    );
  }

  // Sections that don't need standard editor (static content)
  if (section.type === 'how_it_works' || section.type === 'about_wayfinder') {
    return (
      <div className="space-y-3 p-4 rounded-lg border bg-card">
        <h4 className="font-medium text-sm font-['Outfit']">{SECTION_LABELS[section.type]}</h4>
        <div className="space-y-1"><Label className="text-xs">Title</Label><Input value={fields.title || ''} onChange={e => updateField('title', e.target.value)} className="h-8 text-sm" /></div>
        <p className="text-xs text-muted-foreground">This section uses predefined content from the main store page.</p>
      </div>
    );
  }

  // Product Spotlight
  if (section.type === 'product_spotlight') {
    const spotlightProduct = (allProducts || []).find(p => p.id === fields.product_id);
    return (
      <div className="space-y-3 p-4 rounded-lg border bg-card">
        <h4 className="font-medium text-sm font-['Outfit']">Product Spotlight</h4>
        <div className="space-y-1"><Label className="text-xs">Title</Label><Input value={fields.title || ''} onChange={e => updateField('title', e.target.value)} className="h-8 text-sm" /></div>
        <div className="space-y-1">
          <Label className="text-xs">Product</Label>
          <Select value={fields.product_id || '__none__'} onValueChange={v => updateField('product_id', v === '__none__' ? '' : v)}>
            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select product" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">None</SelectItem>
              {(allProducts || []).map(p => <SelectItem key={p.id} value={p.id}>{p.title} ({p.type})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {spotlightProduct && (
          <div className="p-3 rounded-lg border bg-muted/30 text-xs space-y-1">
            <p className="font-medium">{spotlightProduct.title}</p>
            <p className="text-muted-foreground line-clamp-2">{spotlightProduct.short_description}</p>
            <p className="font-semibold text-[#1A9DA5]">${spotlightProduct.price}</p>
          </div>
        )}
      </div>
    );
  }

  // Category Featured Products
  if (section.type === 'category_featured') {
    return (
      <div className="space-y-3 p-4 rounded-lg border bg-card">
        <h4 className="font-medium text-sm font-['Outfit']">Category Featured Products</h4>
        <div className="space-y-1"><Label className="text-xs">Title</Label><Input value={fields.title || ''} onChange={e => updateField('title', e.target.value)} className="h-8 text-sm" /></div>
        <div className="space-y-1">
          <Label className="text-xs">Category</Label>
          <Select value={fields.category_id || '__none__'} onValueChange={v => updateField('category_id', v === '__none__' ? '' : v)}>
            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">None</SelectItem>
              {(allCategories || []).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-muted-foreground">Shows all products marked as "Featured in category" for the selected category.</p>
      </div>
    );
  }

  // Offer Stack - special multi-part editor
  if (section.type === 'offer_stack') {
    const valueStack = fields.value_stack || [];
    const bonuses = fields.bonuses || [];
    return (
      <div className="space-y-3 p-4 rounded-lg border bg-card">
        <div className="flex items-center justify-between gap-2">
          <h4 className="font-medium text-sm font-['Outfit']">Offer Stack</h4>
          <Button variant="ghost" size="sm" onClick={handleRegenerate} disabled={regenerating} className="text-[#1A9DA5] hover:text-[#158D92]">
            <Sparkles className="w-3 h-3 mr-1" />{regenerating ? 'Generating...' : 'Generate'}
          </Button>
        </div>
        <div className="space-y-1"><Label className="text-xs">Section Title</Label><Input value={fields.title || ''} onChange={e => updateField('title', e.target.value)} className="h-8 text-sm" /></div>
        <Separator />
        <Label className="text-xs font-semibold">Value Stack</Label>
        {valueStack.map((item, i) => (
          <div key={i} className="flex gap-1">
            <Input value={item} onChange={e => { const arr = [...valueStack]; arr[i] = e.target.value; updateField('value_stack', arr); }} className="h-8 text-xs" placeholder={`Item ${i+1}`} />
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => updateField('value_stack', valueStack.filter((_, j) => j !== i))}><Trash2 className="w-3 h-3 text-destructive" /></Button>
          </div>
        ))}
        <Button variant="outline" size="sm" className="text-xs" onClick={() => updateField('value_stack', [...valueStack, ''])}><Plus className="w-3 h-3 mr-1" />Add Value Item</Button>
        <Separator />
        <div className="space-y-1"><Label className="text-xs">Price Anchor</Label><Input value={fields.price_anchor || ''} onChange={e => updateField('price_anchor', e.target.value)} className="h-8 text-xs" placeholder="e.g., Total value: $297" /></div>
        <Separator />
        <Label className="text-xs font-semibold">Bonuses</Label>
        {bonuses.map((b, i) => (
          <div key={i} className="flex gap-1">
            <Input value={b} onChange={e => { const arr = [...bonuses]; arr[i] = e.target.value; updateField('bonuses', arr); }} className="h-8 text-xs" placeholder={`Bonus ${i+1}`} />
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => updateField('bonuses', bonuses.filter((_, j) => j !== i))}><Trash2 className="w-3 h-3 text-destructive" /></Button>
          </div>
        ))}
        <Button variant="outline" size="sm" className="text-xs" onClick={() => updateField('bonuses', [...bonuses, ''])}><Plus className="w-3 h-3 mr-1" />Add Bonus</Button>
        <Separator />
        <div className="space-y-1"><Label className="text-xs">Guarantee Title</Label><Input value={fields.guarantee_title || ''} onChange={e => updateField('guarantee_title', e.target.value)} className="h-8 text-xs" /></div>
        <div className="space-y-1"><Label className="text-xs">Guarantee Text</Label><Textarea value={fields.guarantee_text || ''} onChange={e => updateField('guarantee_text', e.target.value)} rows={2} className="text-xs" /></div>
        <div className="space-y-1"><Label className="text-xs">Scarcity</Label><Input value={fields.scarcity || ''} onChange={e => updateField('scarcity', e.target.value)} className="h-8 text-xs" placeholder="e.g., Only 50 copies at this price" /></div>
        <div className="space-y-1"><Label className="text-xs">Urgency</Label><Input value={fields.urgency || ''} onChange={e => updateField('urgency', e.target.value)} className="h-8 text-xs" placeholder="e.g., Offer ends Friday at midnight" /></div>
        <div className="space-y-1"><Label className="text-xs">CTA Text</Label><Input value={fields.cta_text || ''} onChange={e => updateField('cta_text', e.target.value)} className="h-8 text-xs" /></div>
      </div>
    );
  }

  // About the Author
  if (section.type === 'about_author') {
    return (
      <div className="space-y-3 p-4 rounded-lg border bg-card">
        <h4 className="font-medium text-sm font-['Outfit']">About the Author</h4>
        <div className="space-y-1">
          <Label className="text-xs">Display Mode</Label>
          <Select value={fields.display_mode || 'section'} onValueChange={v => updateField('display_mode', v)}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="section">Full Section</SelectItem>
              <SelectItem value="hero_panel">Hero Panel (compact, for inside hero)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-muted-foreground">Uses the Thomas White author bio and profile photo. Select "Hero Panel" to embed as a glass panel inside the hero section, or "Full Section" for a standalone block.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 p-4 rounded-lg border bg-card">
      <div className="flex items-center justify-between gap-2">
        <h4 className="font-medium text-sm font-['Outfit']">{SECTION_LABELS[section.type] || section.type}</h4>
        <div className="flex items-center gap-2">
          {countConfig && (
            <Input type="number" min={countConfig.min} max={countConfig.max} value={genCount} onChange={e => setGenCount(parseInt(e.target.value) || countConfig.default)} className="h-7 w-14 text-xs text-center" title={`Number of ${countConfig.label.toLowerCase()} to generate`} />
          )}
          <Button variant="ghost" size="sm" onClick={handleRegenerate} disabled={regenerating} className="text-[#1A9DA5] hover:text-[#158D92]">
            <Sparkles className="w-3 h-3 mr-1" />{regenerating ? 'Generating...' : 'Generate'}
          </Button>
        </div>
      </div>
      {Object.entries(fields).map(([key, val]) => {
        if (key === 'section_image') {
          return <ImageUploader key={key} value={val || ''} onChange={url => updateField('section_image', url)} label="Section Image" />;
        }
        if (key === 'items' && Array.isArray(val)) {
          if (val.length > 0 && typeof val[0] === 'object') {
            return (
              <div key={key} className="space-y-2">
                <Label className="text-xs capitalize">{key}</Label>
                {val.map((item, i) => (
                  <div key={i} className="p-2 rounded border space-y-1">
                    {Object.entries(item).map(([ik, iv]) => (
                      <div key={ik}><Label className="text-xs">{ik}</Label><Input value={iv || ''} onChange={e => { const newItems = [...val]; newItems[i] = { ...item, [ik]: e.target.value }; updateField('items', newItems); }} className="h-8 text-xs" /></div>
                    ))}
                    <Button variant="ghost" size="sm" className="text-destructive text-xs" onClick={() => updateField('items', val.filter((_, j) => j !== i))}>Remove</Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="text-xs" onClick={() => {
                  const template = val[0] ? Object.fromEntries(Object.keys(val[0]).map(k => [k, ''])) : { text: '' };
                  updateField('items', [...val, template]);
                }}>+ Add Item</Button>
              </div>
            );
          }
          return (
            <div key={key} className="space-y-1">
              <Label className="text-xs capitalize">{key}</Label>
              <Textarea value={val.join('\n')} onChange={e => updateField('items', e.target.value.split('\n'))} rows={3} className="text-sm" placeholder="One item per line" />
            </div>
          );
        }
        if (key === 'features' && Array.isArray(val)) {
          return (
            <div key={key} className="space-y-1">
              <Label className="text-xs capitalize">{key}</Label>
              <Textarea value={val.join('\n')} onChange={e => updateField('features', e.target.value.split('\n'))} rows={3} className="text-sm" placeholder="One feature per line" />
            </div>
          );
        }
        if (typeof val === 'boolean') return null; // handled above for hero
        if (typeof val === 'string') {
          if (key === 'content') {
            return (
              <div key={key} className="space-y-1">
                <Label className="text-xs capitalize">Content</Label>
                <RichTextEditor value={val} onChange={v => updateField('content', v)} />
              </div>
            );
          }
          const isLong = val.length > 100 || key === 'description';
          return (
            <div key={key} className="space-y-1">
              <Label className="text-xs capitalize">{key.replace(/_/g, ' ')}</Label>
              {isLong ? <Textarea value={val} onChange={e => updateField(key, e.target.value)} rows={3} className="text-sm" /> : <Input value={val} onChange={e => updateField(key, e.target.value)} className="h-8 text-sm" />}
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}

export default function PageEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [product, setProduct] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [showRevisions, setShowRevisions] = useState(false);

  useEffect(() => {
    if (id) {
      api.get(`/pages/${id}`).then(r => {
        setPage(r.data);
        if (r.data.product_id) {
          api.get(`/products/${r.data.product_id}`).then(pr => setProduct(pr.data)).catch(() => {});
        }
      }).catch(() => toast.error('Page not found'));
      api.get(`/pages/${id}/revisions`).then(r => setRevisions(r.data)).catch(() => {});
    }
    api.get('/products?status=published').then(r => setAllProducts(r.data || [])).catch(() => {});
    api.get('/categories').then(r => setAllCategories(r.data || [])).catch(() => {});
  }, [id]);

  if (!page) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-6 h-6 border-2 border-[#1A9DA5] border-t-transparent rounded-full" /></div>;

  const sections = page.sections || [];

  const updateSection = (index, updated) => {
    const newSections = [...sections];
    newSections[index] = updated;
    setPage({ ...page, sections: newSections });
  };

  const moveSection = (index, dir) => {
    const newSections = [...sections];
    const target = index + dir;
    if (target < 0 || target >= newSections.length) return;
    [newSections[index], newSections[target]] = [newSections[target], newSections[index]];
    newSections.forEach((s, i) => { s.order = i; });
    setPage({ ...page, sections: newSections });
  };

  const toggleSection = (index) => {
    const newSections = [...sections];
    newSections[index] = { ...newSections[index], enabled: !newSections[index].enabled };
    setPage({ ...page, sections: newSections });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/pages/${id}`, page);
      toast.success('Page saved');
      api.get(`/pages/${id}/revisions`).then(r => setRevisions(r.data)).catch(() => {});
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const regenerateSection = async (sectionId, instructions = '') => {
    try {
      const { data } = await api.post('/ai/regenerate-section', { page_id: id, section_id: sectionId, instructions });
      setPage(data);
      toast.success('Section regenerated');
    } catch { toast.error('Regeneration failed'); }
  };

  const revertToRevision = async (revisionId) => {
    try {
      const { data } = await api.post(`/pages/${id}/revert/${revisionId}`);
      setPage(data);
      toast.success('Reverted to previous version');
      setShowRevisions(false);
    } catch { toast.error('Revert failed'); }
  };

  const addSectionFromProduct = (type, title, data) => {
    const existing = sections.findIndex(s => s.type === type);
    if (existing >= 0) {
      const newSections = [...sections];
      newSections[existing] = { ...newSections[existing], enabled: true, fields: { ...newSections[existing].fields, ...data } };
      setPage({ ...page, sections: newSections });
      toast.success(`${title} section updated with product data`);
    } else {
      const newSection = { id: `${type}-${Date.now()}`, type, enabled: true, order: sections.length, fields: data };
      setPage({ ...page, sections: [...sections, newSection] });
      toast.success(`${title} section added from product data`);
    }
  };

  const addOrEnableSection = (type, defaultFields) => {
    const existing = sections.findIndex(s => s.type === type);
    if (existing >= 0) {
      if (!sections[existing].enabled) toggleSection(existing);
      toast.info(`${SECTION_LABELS[type]} section enabled`);
    } else {
      const newSection = { id: `${type}-${Date.now()}`, type, enabled: true, order: sections.length, fields: defaultFields };
      setPage({ ...page, sections: [...sections, newSection] });
      toast.success(`${SECTION_LABELS[type]} section added`);
    }
  };

  return (
    <div className="space-y-6" data-testid="page-editor">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/pages')}><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h1 className="text-2xl font-bold font-['Outfit']">Edit Page</h1>
            <p className="text-sm text-muted-foreground">/{page.slug}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Dialog open={showRevisions} onOpenChange={setShowRevisions}>
            <DialogTrigger asChild><Button variant="outline" size="sm"><History className="w-4 h-4 mr-1" />Revisions ({revisions.length})</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Page Revisions</DialogTitle></DialogHeader>
              <ScrollArea className="max-h-80">
                {revisions.map(r => (
                  <div key={r.id} className="flex items-center justify-between p-3 border-b">
                    <span className="text-sm">{new Date(r.created_at).toLocaleString()}</span>
                    <Button variant="outline" size="sm" onClick={() => revertToRevision(r.id)}><RotateCcw className="w-3 h-3 mr-1" />Revert</Button>
                  </div>
                ))}
                {!revisions.length && <p className="text-sm text-muted-foreground p-4">No revisions yet</p>}
              </ScrollArea>
            </DialogContent>
          </Dialog>
          <Button variant="outline" size="sm" onClick={() => window.open(`/l/${page.slug}`, '_blank')}><Eye className="w-4 h-4 mr-1" />Preview</Button>
          <Button data-testid="save-page-btn" size="sm" onClick={save} disabled={saving} className="bg-[#1A9DA5] hover:bg-[#158D92] text-white">
            <Save className="w-4 h-4 mr-1" />{saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Page Settings</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1"><Label>Title</Label><Input value={page.title || ''} onChange={e => setPage({ ...page, title: e.target.value })} /></div>
              <div className="space-y-1"><Label>Slug</Label><Input value={page.slug || ''} onChange={e => setPage({ ...page, slug: e.target.value })} /></div>
              <div className="space-y-1">
                <Label>Template</Label>
                <Select value={page.template_type} onValueChange={v => setPage({ ...page, template_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="problem_solution">Problem/Solution</SelectItem>
                    <SelectItem value="case_study">Case Study</SelectItem>
                    <SelectItem value="checklist">Checklist</SelectItem>
                    <SelectItem value="webinar_style">Webinar Style</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Status</Label>
                <Select value={page.status} onValueChange={v => setPage({ ...page, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Sections</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {sections.map((s, i) => (
                <div key={s.id} className="flex items-center gap-2 p-2 rounded hover:bg-accent text-sm">
                  <Switch checked={s.enabled} onCheckedChange={() => toggleSection(i)} />
                  <span className={`flex-1 ${!s.enabled ? 'text-muted-foreground line-through' : ''}`}>{SECTION_LABELS[s.type] || s.type}</span>
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveSection(i, -1)} disabled={i === 0}><ChevronUp className="w-3 h-3" /></Button>
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveSection(i, 1)} disabled={i === sections.length - 1}><ChevronDown className="w-3 h-3" /></Button>
                </div>
              ))}
              <Separator className="my-2" />
              <p className="text-xs font-medium text-muted-foreground mb-1">Add sections</p>
              <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => addOrEnableSection('audio_teaser', { title: 'Listen to a Preview', description: '' })}>
                <Download className="w-3 h-3 mr-1 text-[#E8735A]" />Audio Teaser
              </Button>
              <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => addOrEnableSection('external_link', { title: 'Companion Tool', description: 'Companion tool for this guide' })}>
                <Download className="w-3 h-3 mr-1 text-[#1A9DA5]" />Companion Tool
              </Button>
              <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => addOrEnableSection('how_it_works', { title: 'Get Started in 3 Easy Steps' })}>
                <Download className="w-3 h-3 mr-1 text-[#1A9DA5]" />How It Works
              </Button>
              <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => addOrEnableSection('about_wayfinder', { title: 'Your Personal Business Advisor, On Demand' })}>
                <Download className="w-3 h-3 mr-1 text-[#1A9DA5]" />About Wayfinder
              </Button>
              <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => {
                const newSection = { id: `spotlight-${Date.now()}`, type: 'product_spotlight', enabled: true, order: sections.length, fields: { product_id: '', title: 'Featured Product' } };
                setPage({ ...page, sections: [...sections, newSection] });
                toast.success('Product Spotlight section added');
              }}>
                <Download className="w-3 h-3 mr-1 text-[#E8735A]" />Product Spotlight
              </Button>
              <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => {
                const catId = page.category_id || '';
                const newSection = { id: `catfeat-${Date.now()}`, type: 'category_featured', enabled: true, order: sections.length, fields: { title: 'Featured Products', category_id: catId } };
                setPage({ ...page, sections: [...sections, newSection] });
                toast.success('Category Featured Products section added');
              }}>
                <Download className="w-3 h-3 mr-1 text-[#E8735A]" />Category Featured
              </Button>
              <Separator className="my-1" />
              <p className="text-xs font-medium text-muted-foreground mb-1">Sales sections</p>
              <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => addOrEnableSection('problem_agitation', { title: 'Does This Sound Like You?', items: [] })}>
                <Download className="w-3 h-3 mr-1 text-[#E8735A]" />Problem Agitation
              </Button>
              <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => addOrEnableSection('solution_mechanism', { title: 'Here\'s How It Works', description: '', items: [] })}>
                <Download className="w-3 h-3 mr-1 text-[#E8735A]" />Solution/Mechanism
              </Button>
              <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => addOrEnableSection('social_proof', { title: 'Join Thousands of Business Owners', items: [], stats: '' })}>
                <Download className="w-3 h-3 mr-1 text-[#E8735A]" />Social Proof
              </Button>
              <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => addOrEnableSection('offer_stack', { title: 'Everything You Get Today', value_stack: [], price_anchor: '', bonuses: [], guarantee_title: '', guarantee_text: '', scarcity: '', urgency: '', cta_text: 'Get Instant Access' })}>
                <Download className="w-3 h-3 mr-1 text-[#E8735A]" />Offer Stack
              </Button>
              <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => addOrEnableSection('final_cta', { title: 'Ready to Transform Your Business?', description: '', cta_text: 'Get Started Now', cta_url: '#pricing' })}>
                <Download className="w-3 h-3 mr-1 text-[#E8735A]" />Final CTA
              </Button>
              <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => addOrEnableSection('pdf_content', { title: 'What\'s Inside the Guide', description: '', items: [], page_count: '', format: 'PDF' })}>
                <Download className="w-3 h-3 mr-1 text-[#1A9DA5]" />PDF Content Details
              </Button>
              <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => addOrEnableSection('about_author', { display_mode: 'section' })}>
                <Download className="w-3 h-3 mr-1 text-[#1A9DA5]" />About the Author
              </Button>
              {product && (
                <>
                  <Separator className="my-1" />
                  <p className="text-xs font-medium text-muted-foreground mb-1">From product</p>
                  {product.outcomes?.length > 0 && (
                    <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => addSectionFromProduct('bullets', "What You'll Get", { title: "What You'll Get", items: product.outcomes })}>
                      <Download className="w-3 h-3 mr-1 text-[#1A9DA5]" />What You'll Get ({product.outcomes.length})
                    </Button>
                  )}
                  {product.long_description && (
                    <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => addSectionFromProduct('custom_rich_text', 'About This Guide', { title: 'About This Guide', content: product.long_description })}>
                      <Download className="w-3 h-3 mr-1 text-[#1A9DA5]" />About This Guide
                    </Button>
                  )}
                  {product.faq?.length > 0 && (
                    <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => addSectionFromProduct('faq', 'FAQ', { title: 'Frequently Asked Questions', items: product.faq })}>
                      <Download className="w-3 h-3 mr-1 text-[#1A9DA5]" />FAQ ({product.faq.length})
                    </Button>
                  )}
                  {product.testimonials?.length > 0 && (
                    <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => addSectionFromProduct('testimonials', 'Testimonials', { title: 'What Others Say', items: product.testimonials })}>
                      <Download className="w-3 h-3 mr-1 text-[#1A9DA5]" />Testimonials ({product.testimonials.length})
                    </Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-2 space-y-4">
          {sections.filter(s => s.enabled).sort((a, b) => a.order - b.order).map((s) => {
            const originalIndex = sections.indexOf(s);
            return (
              <SectionEditor
                key={s.id}
                section={s}
                onChange={(updated) => updateSection(originalIndex, updated)}
                onRegenerate={regenerateSection}
                allProducts={allProducts}
                allCategories={allCategories}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
